// Cap the length of any single allow-listed field we record, as a defensive
// guard against unexpectedly large values.
const MAX_ERROR_FIELD_LENGTH = 200;

/**
 * Extract structured, allow-listed error details from a backend error payload.
 *
 * IMPORTANT (privacy): backend error bodies can echo user-generated content
 * (e.g. the user's search query reflected in a validation message, or an
 * unstructured HTML/stack-trace body). To avoid recording UGC into analytics we
 * do NOT record the raw body. We only extract a small set of known,
 * system-generated fields (error key/type and status code). The full body
 * remains retrievable from backend/gateway logs via the trace id.
 *
 * Returns a compact, non-UGC string suitable for the `details` field, or
 * undefined when nothing safe could be extracted.
 */
export function extractStructuredErrorDetails(rawBody: string | undefined): string | undefined {
	if (!rawBody) {
		return undefined;
	}
	try {
		const parsed = JSON.parse(rawBody);
		const errorObj = (parsed?.error ?? parsed) as Record<string, unknown> | undefined;
		if (!errorObj || typeof errorObj !== 'object') {
			return undefined;
		}
		const clip = (value: unknown): string | undefined =>
			typeof value === 'string' && value.length > 0
				? value.slice(0, MAX_ERROR_FIELD_LENGTH)
				: typeof value === 'number'
					? String(value)
					: undefined;
		// Allow-list of non-UGC identifiers only. Deliberately excludes free-form
		// message text, which may contain user-generated content.
		const key = clip(errorObj.key ?? errorObj.type ?? errorObj.code);
		const statusCode = clip(errorObj.statusCode ?? errorObj.status);
		const safe: Record<string, string> = {};
		if (key) {
			safe.key = key;
		}
		if (statusCode) {
			safe.statusCode = statusCode;
		}
		return Object.keys(safe).length > 0 ? JSON.stringify(safe) : undefined;
	} catch {
		// Non-JSON body (e.g. HTML error page) - do not record it, to avoid UGC.
		return undefined;
	}
}
